import { labelEntry, valueLabel } from "./formatters";
import { orderSeries } from "./palette";

/**
 * The grid a MatrixChart draws (ASB-4377, A.6 `chart: "matrix"`): the
 * dataset names a `rows` column, a `cols` column, a summed `value` column and
 * an optional `detail` column. Each cell sums `value` over the API rows that
 * share (rows, cols), carries its share of the grand total and, with
 * `detail`, the split of that sum by the detail column's values. Nothing
 * here reads the DOM, so it is unit-tested directly.
 *
 * Headers come in the order the column's `labels` map lists its values (the
 * manifest's reading order), then any unlisted value as the palette orders
 * series (roles first, then sorted). A header carries the value's label and
 * help.
 *
 * A cell drills (A.6) only when every row behind it agrees on each key of
 * `drill.on` and none of those values is null or missing (A.7: a null drill
 * key doesn't drill — rules_check stores `kind` null for the two agreement
 * cells, which keep no samples); then `drill` is `{ key: value }`, otherwise
 * null.
 */
const asKey = (v) => String(v ?? "");

function headerOrder(column, values) {
	const listed = Object.keys(column?.labels ?? {});
	const seen = new Set(values.map(asKey));
	const first = listed.filter((v) => seen.has(v));
	const rest = orderSeries(
		values.filter((v) => !listed.includes(asKey(v))),
		column?.roles
	);
	return [...first, ...rest].map((value) => ({
		value,
		label: valueLabel(column, value),
		help: labelEntry(column, value)?.help ?? null,
	}));
}

export function buildMatrix(dataset, rows) {
	const columns = dataset.columns ?? [];
	const find = (key) => columns.find((c) => c.key === key) ?? (key ? { key, type: "text" } : null);
	const rowCol = find(dataset.rows);
	const colCol = find(dataset.cols);
	const valueCol = find(dataset.value) ?? { key: dataset.value, type: "int", label: "Records" };
	const detailCol = dataset.detail ? find(dataset.detail) : null;
	const drillOn = dataset.drill?.on ?? [];
	if (!rowCol || !colCol || !dataset.value) {
		return { rowColumn: rowCol, colColumn: colCol, valueColumn: valueCol, rows: [], cols: [], cells: new Map(), total: 0 };
	}

	const cells = new Map();
	let total = 0;
	for (const r of rows ?? []) {
		const n = Number(r[valueCol.key]);
		if (!Number.isFinite(n)) continue;
		const key = `${asKey(r[rowCol.key])}\u0000${asKey(r[colCol.key])}`;
		let cell = cells.get(key);
		if (!cell) {
			cell = {
				row: asKey(r[rowCol.key]),
				col: asKey(r[colCol.key]),
				value: 0,
				details: new Map(),
				drill: {},
				agrees: true,
			};
			cells.set(key, cell);
		}
		cell.value += n;
		total += n;
		if (detailCol) {
			const d = r[detailCol.key];
			if (d !== null && d !== undefined && d !== "") {
				cell.details.set(asKey(d), (cell.details.get(asKey(d)) ?? 0) + n);
			}
		}
		for (const k of drillOn) {
			const v = r[k] ?? null;
			if (!(k in cell.drill)) cell.drill[k] = v;
			else if (asKey(cell.drill[k]) !== asKey(v)) cell.agrees = false;
		}
	}

	const rowValues = Array.from(new Set(Array.from(cells.values()).map((c) => c.row)));
	const colValues = Array.from(new Set(Array.from(cells.values()).map((c) => c.col)));
	const out = {
		rowColumn: rowCol,
		colColumn: colCol,
		valueColumn: valueCol,
		detailColumn: detailCol,
		rows: headerOrder(rowCol, rowValues),
		cols: headerOrder(colCol, colValues),
		total,
		cells: new Map(),
	};
	for (const cell of cells.values()) {
		const keyed = drillOn.length > 0 && cell.agrees && drillOn.every((k) => cell.drill[k] !== null);
		const details = detailCol
			? headerOrder(
					detailCol,
					Array.from(cell.details.keys())
			  ).map((h) => ({ ...h, n: cell.details.get(h.value) ?? 0 }))
			: [];
		out.cells.set(`${cell.row}\u0000${cell.col}`, {
			row: cell.row,
			col: cell.col,
			value: cell.value,
			share: total > 0 ? cell.value / total : null,
			details,
			drill: keyed ? cell.drill : null,
		});
	}
	return out;
}

/** The cell at (row value, col value), or null where the range had no rows. */
export const cellAt = (matrix, row, col) => matrix.cells.get(`${asKey(row)}\u0000${asKey(col)}`) ?? null;
